/**
 * A small RFC 4180 parser for CSV artefacts (design §4): quoted fields, doubled quotes, LF or
 * CRLF line ends. Pure and linear; cells stay strings (a formula such as `=SUM(A1)` is text).
 * The server already refused malformed CSV; whatever slips through still parses to rows.
 */

export interface ParsedCsv {
  rows: string[][];
  /** Data rows (after the header) in the whole content. */
  total: number;
}

/** Parses `text`, keeping at most `limit` data rows after the header (all are counted). */
export function parseCsv(text: string, limit = Number.POSITIVE_INFINITY): ParsedCsv {
  const rows: string[][] = [];
  let total = -1; // the header is not a data row
  let row: string[] = [];
  let field = "";
  let quoted = false;
  // The current record has begun (a quoted empty field leaves `field` empty).
  let started = false;
  let i = 0;
  const endRow = () => {
    row.push(field);
    field = "";
    started = false;
    total++;
    if (total <= limit) {
      rows.push(row);
    }
    row = [];
  };
  while (i < text.length) {
    const ch = text[i] as string;
    if (ch !== "\n" && ch !== "\r") {
      started = true;
    }
    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        quoted = false;
      } else {
        field += ch;
      }
      i++;
      continue;
    }
    if (ch === '"' && field === "") {
      quoted = true;
    } else if (ch === ",") {
      row.push(field);
      field = "";
    } else if (ch === "\r" && text[i + 1] === "\n") {
      endRow();
      i++;
    } else if (ch === "\n") {
      endRow();
    } else {
      field += ch;
    }
    i++;
  }
  // The last line, unless the content ends with a line end.
  if (started || row.length > 0 || quoted) {
    endRow();
  }
  return { rows, total: Math.max(total, 0) };
}

/** Data rows shown in the panel; the rest are counted and downloadable. */
export const CSV_ROWS_SHOWN = 1_000;
/** Cells shown in the panel at most: a wide table shows fewer rows, never more cells. */
export const CSV_CELLS_SHOWN = 25_000;
